import { CrewDateFilterBox } from 'components/devextreme/crewDateFilterBox'
import { CrewSelectBox } from 'components/devextreme/crewSelectBox'
import { CrewTagBox } from 'components/devextreme/crewTagbox/crewTagBox'
import { CrewTextBox } from 'components/devextreme/crewTextBox'
import { FC, memo } from 'react'
import { assertNever } from '@crew/utils'
import _ from 'lodash'
import { useCallback, useMemo } from 'react'
import {
  ParamValue,
  SearchFilter,
  dateFilterValueToString,
  stringToDateFilterValue,
} from 'utils/filter'
import { ValueChangedEvent as TextBoxValueChangedEvent } from 'devextreme/ui/text_box'
import { ValueChangedEvent as SelectBoxValueChangedEvent } from 'devextreme/ui/select_box'
import { ValueChangedEvent as TagBoxValueChangedEvent } from 'devextreme/ui/tag_box'
import { CrewDateFilterBoxValueChangedEvent } from 'components/devextreme/crewDateFilterBox'
import {
  useTaskPriorityDataSource,
  useTaskStateTypeDataSource,
} from 'hooks/dataSource/useResourceDataSource'
import { useTaskKindDataSource } from 'hooks/dataSource/useTaskKindDataSource'
import { useTaskStateDataSource } from 'hooks/dataSource/useTaskStateDataSource'
import { EntityType } from '@crew/enums/domain'
import { useEntityRecordDataSource } from 'hooks/dataSource/useEntityRecordDataSource'
import { useTaskCategoryDataSource } from 'hooks/dataSource/useTaskCategoryDataSource'
import { TaskSearchOptions } from 'enums/app'
import { getParamAsString } from 'utils'
import { useSearchParams } from 'react-router-dom'
import qs from 'qs'
import { useProjectGroupDataSource } from 'hooks/dataSource/useProjectGroupDataSource'
import { useUserDataSource } from 'hooks/dataSource/useUserDataSource'
import { t } from '@crew/modules/i18n'
import { TaskStateRef, TaskKindRef, TaskCategoryRef } from '@crew/models/refs'

type TaskSearchInputProps = {
  filter: SearchFilter
  updateSearchValue: (filter: SearchFilter, value: ParamValue) => void
}

type GroupComponentType<T> = {
  data: {
    items: T[]
    key: string
  }
}

// Render task type group name
const renderTaskKindGroupName = ({ data }: GroupComponentType<TaskKindRef>) => {
  // プロジェクトに紐づかないデータは仕様上ありえないが、型制約の関係上チェック処理を追加
  const item = data.items.find((item) => item.projectId)

  if (!item) {
    return null
  }

  return <span>{item.projectName}</span>
}

// Render task category group name
const renderTaskCategoryGroupName = ({
  data,
}: GroupComponentType<TaskCategoryRef>) => {
  // プロジェクトに紐づかないデータは仕様上ありえないが、型制約の関係上チェック処理を追加
  const item = data.items.find((item) => item.projectId)

  if (!item) {
    return null
  }

  return <span>{item.projectName}</span>
}

// Render task state group name
const renderTaskStateGroupName = ({
  data,
}: GroupComponentType<TaskStateRef>) => {
  const item = data.items.find((item) => item.projectId)

  // プロジェクトに紐づかないデータは仕様上ありえないが、型制約の関係上チェック処理を追加
  if (!item) {
    return null
  }

  return <span>{item.projectName}</span>
}

export const CrewTaskSearchInput: FC<TaskSearchInputProps> = memo(
  ({ filter, updateSearchValue }) => {
    const [searchParams] = useSearchParams()

    const params = useMemo(
      () => qs.parse(searchParams.toString()),
      [searchParams]
    )

    const projectId = useMemo(() => {
      return getParamAsString(TaskSearchOptions.ProjectId.id, params)
    }, [params])

    // タスク優先度のカスタムデータソース
    const taskPriorityDataSource = useTaskPriorityDataSource()
    // 実際にタスク種別のリストに表示されるデータ
    const taskKindDataSource = useTaskKindDataSource(
      EntityType.Project,
      projectId,
      false, // hasBaseFilter
      true, // isGrouped
      true // multiSelect
    )
    // custom data source for assign to user
    const assignToUserDataSource = useUserDataSource(true)

    // custom data source for task state
    const taskStateDataSource = useTaskStateDataSource(
      EntityType.Project,
      projectId,
      true
    )
    // custom data source for related item
    const projectDataSource = useEntityRecordDataSource(EntityType.Project)
    // custom data source for project group
    const projectGroupDataSource = useProjectGroupDataSource()
    // custom data source for task category
    const taskCategoryDataSource = useTaskCategoryDataSource(
      EntityType.Project,
      projectId,
      true
    )
    // custom data source for created by user
    const createdByUserDataSource = useUserDataSource(true)
    // custom data source for updated by user
    const updatedByUserDataSource = useUserDataSource(true)

    // タスク状態タイプのカスタムデータソース
    const taskStateTypeDataSource = useTaskStateTypeDataSource()

    // キーワードの変更
    const handleKeywordChanged = useCallback(
      (e: TextBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        if (e.value === '') {
          updateSearchValue(filter, undefined)
          return
        }
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // タスク種別の変更
    const handleTypeChanged = useCallback(
      (e: TagBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 優先度の変更
    const handlePriorityChanged = useCallback(
      (e: TagBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 担当者の変更
    const handleAssignToChanged = useCallback(
      (e: SelectBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // ステータスの変更
    const handleStatusChanged = useCallback(
      (e: TagBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 開始日の変更
    const handleStartDateChanged = useCallback(
      (e: CrewDateFilterBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        if (e.value) {
          updateSearchValue(filter, dateFilterValueToString(e.value))
        }
      },
      [filter, updateSearchValue]
    )

    // 期限日の変更
    const handleDueDateChanged = useCallback(
      (e: CrewDateFilterBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        if (e.value) {
          updateSearchValue(filter, dateFilterValueToString(e.value))
        }
      },
      [filter, updateSearchValue]
    )

    // プロジェクトの変更
    const handleProjectChanged = useCallback(
      (e: SelectBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // カテゴリの変更
    const handleCategoryChanged = useCallback(
      (e: TagBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 作成者の変更
    const handleCreatedByChanged = useCallback(
      (e: SelectBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 更新者の変更
    const handleUpdatedByChanged = useCallback(
      (e: SelectBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 作成日の変更
    const handleCreatedAtChanged = useCallback(
      (e: CrewDateFilterBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        if (e.value) {
          updateSearchValue(filter, dateFilterValueToString(e.value))
        }
      },
      [filter, updateSearchValue]
    )

    // 更新日の変更
    const handleUpdatedAtChanged = useCallback(
      (e: CrewDateFilterBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        if (e.value) {
          updateSearchValue(filter, dateFilterValueToString(e.value))
        }
      },
      [filter, updateSearchValue]
    )

    // プロジェクトグループの変更
    const handleProjectGroupChanged = useCallback(
      (e: TagBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // タスク状態タイプの変更
    const handleTaskStateTypeChanged = useCallback(
      (e: TagBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    switch (filter.field) {
      case TaskSearchOptions.Keyword.id:
        return (
          <CrewTextBox
            id={TaskSearchOptions.Keyword.id}
            name={TaskSearchOptions.Keyword.name}
            value={filter.value as string}
            showClearButton={true}
            onValueChanged={handleKeywordChanged}
            mode="search"
          />
        )
      case TaskSearchOptions.TaskKindId.id:
        return (
          <CrewTagBox
            id={TaskSearchOptions.TaskKindId.id}
            name={TaskSearchOptions.TaskKindId.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={taskKindDataSource}
            searchEnabled={true}
            popupSearchEnabled={true}
            searchMode="contains"
            searchExpr="name"
            minSearchLength={0}
            maxDisplayedTags={2}
            value={filter.value as string[]}
            onValueChanged={handleTypeChanged}
            grouped
            groupComponent={renderTaskKindGroupName}
          />
        )
      case TaskSearchOptions.AssignToUser.id:
        return (
          <CrewSelectBox
            id={TaskSearchOptions.AssignToUser.id}
            name={TaskSearchOptions.AssignToUser.name}
            dataSource={assignToUserDataSource}
            displayExpr="displayName"
            valueExpr="id"
            minSearchLength={0}
            showClearButton={true}
            value={filter.value}
            onValueChanged={handleAssignToChanged}
            searchEnabled={true}
            searchExpr="displayName"
          />
        )
      case TaskSearchOptions.TaskStateId.id:
        return (
          <CrewTagBox
            id={TaskSearchOptions.TaskStateId.id}
            name={TaskSearchOptions.TaskStateId.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={taskStateDataSource}
            searchEnabled={true}
            popupSearchEnabled={true}
            searchMode="contains"
            searchExpr="name"
            minSearchLength={0}
            maxDisplayedTags={2}
            value={filter.value as string[]}
            onValueChanged={handleStatusChanged}
            grouped
            groupComponent={renderTaskStateGroupName}
          />
        )

      case TaskSearchOptions.TaskStateType.id:
        return (
          <CrewTagBox
            id={TaskSearchOptions.TaskStateType.id}
            name={TaskSearchOptions.TaskStateType.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={taskStateTypeDataSource}
            searchEnabled={true}
            popupSearchEnabled={true}
            searchMode="contains"
            searchExpr="name"
            minSearchLength={0}
            maxDisplayedTags={2}
            value={filter.value as string[]}
            onValueChanged={handleTaskStateTypeChanged}
          />
        )

      case TaskSearchOptions.TaskPriority.id:
        return (
          <CrewTagBox
            id={TaskSearchOptions.TaskPriority.id}
            name={TaskSearchOptions.TaskPriority.name}
            displayExpr="name"
            valueExpr={(item) => item && String(item.id)} // we need to cast priority to string
            dataSource={taskPriorityDataSource}
            searchEnabled={true}
            popupSearchEnabled={true}
            searchMode="contains"
            searchExpr="name"
            minSearchLength={0}
            maxDisplayedTags={2}
            value={filter.value as string[]}
            onValueChanged={handlePriorityChanged}
          />
        )
      case TaskSearchOptions.TaskCategoryId.id:
        return (
          <CrewTagBox
            id={TaskSearchOptions.TaskCategoryId.id}
            name={TaskSearchOptions.TaskCategoryId.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={taskCategoryDataSource}
            searchEnabled={true}
            popupSearchEnabled={true}
            searchMode="contains"
            searchExpr="name"
            minSearchLength={0}
            maxDisplayedTags={2}
            value={filter.value as string[]}
            onValueChanged={handleCategoryChanged}
            noDataText={
              projectId
                ? t('label.noDataText')
                : t('message.general.selectProject')
            }
            grouped
            groupComponent={renderTaskCategoryGroupName}
          />
        )

      case TaskSearchOptions.ProjectId.id:
        return (
          <CrewSelectBox
            id={TaskSearchOptions.ProjectId.id}
            name={TaskSearchOptions.ProjectId.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={projectDataSource}
            minSearchLength={0}
            showClearButton={true}
            value={filter.value}
            onValueChanged={handleProjectChanged}
            searchEnabled={true}
            searchExpr="name"
          />
        )
      case TaskSearchOptions.ProjectGroupId.id:
        return (
          <CrewTagBox
            id={TaskSearchOptions.ProjectGroupId.id}
            name={TaskSearchOptions.ProjectGroupId.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={projectGroupDataSource}
            searchEnabled={true}
            popupSearchEnabled={true}
            searchMode="contains"
            searchExpr="name"
            minSearchLength={0}
            maxDisplayedTags={2}
            value={filter.value as string[]}
            onValueChanged={handleProjectGroupChanged}
          />
        )
      case TaskSearchOptions.StartDate.id:
        return (
          <CrewDateFilterBox
            value={stringToDateFilterValue(filter.value as string)}
            onValueChange={handleStartDateChanged}
          />
        )
      case TaskSearchOptions.DueDate.id:
        return (
          <CrewDateFilterBox
            value={stringToDateFilterValue(filter.value as string)}
            onValueChange={handleDueDateChanged}
            showOverdueOption
          />
        )
      case TaskSearchOptions.CreatedById.id:
        return (
          <CrewSelectBox
            id={TaskSearchOptions.CreatedById.id}
            name={TaskSearchOptions.CreatedById.name}
            displayExpr="displayName"
            valueExpr="id"
            dataSource={createdByUserDataSource}
            minSearchLength={0}
            showClearButton={true}
            value={filter.value}
            onValueChanged={handleCreatedByChanged}
            searchEnabled={true}
            searchExpr="displayName"
          />
        )
      case TaskSearchOptions.UpdatedById.id:
        return (
          <CrewSelectBox
            id={TaskSearchOptions.UpdatedById.id}
            name={TaskSearchOptions.UpdatedById.name}
            displayExpr="displayName"
            valueExpr="id"
            dataSource={updatedByUserDataSource}
            minSearchLength={0}
            showClearButton={true}
            value={filter.value}
            onValueChanged={handleUpdatedByChanged}
            searchEnabled={true}
            searchExpr="displayName"
          />
        )
      case TaskSearchOptions.CreatedAt.id:
        return (
          <CrewDateFilterBox
            value={stringToDateFilterValue(filter.value as string)}
            onValueChange={handleCreatedAtChanged}
          />
        )
      case TaskSearchOptions.UpdatedAt.id:
        return (
          <CrewDateFilterBox
            value={stringToDateFilterValue(filter.value as string)}
            onValueChange={handleUpdatedAtChanged}
          />
        )
      default:
        // assertNever has value type never, so we need to cast filter.field to never
        assertNever(filter.field as never, 'Invalid filter field')
    }
  }
)
